import PropTypes from 'prop-types';
import React, { memo } from 'react';
import avatarSvg from '../../../../assets/avatar.svg';
import iPhoneFrame from '../../../../assets/iphone_frame.png';
import '../../styles/styles.scss';

const WhatsAppTemplatePreview = memo(
  ({
    header,
    headerType,
    headerMediaUrl,
    body,
    footer,
    buttons,
    variableEntries = [],
    isAuthentication,
    securityRecommendation,
    codeExp,
    showExpiryTime,
  }) => {
    const renderHeader = () => {
      switch (headerType) {
        case 'IMAGE':
          return (
            <div
              className="header-media"
              style={{
                width: '100%',
                marginBottom: '8px',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                overflow: 'hidden',
              }}
            >
              {headerMediaUrl ? (
                <img
                  src={headerMediaUrl}
                  alt="Header"
                  style={{
                    width: '100%',
                    aspectRatio: '1.91/1',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    aspectRatio: '1.91/1',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#f2f2f2',
                    color: '#667781',
                  }}
                >
                  <i className="fas fa-image" style={{ fontSize: '24px', marginBottom: '8px' }}></i>
                  <span style={{ fontSize: '14px' }}>Image</span>
                </div>
              )}
            </div>
          );
        case 'VIDEO':
          return (
            <div
              className="header-media"
              style={{
                width: '100%',
                marginBottom: '8px',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                overflow: 'hidden',
              }}
            >
              {headerMediaUrl ? (
                <video
                  src={headerMediaUrl}
                  controls
                  style={{
                    width: '100%',
                    aspectRatio: '1.91/1',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div
                  style={{
                    width: '100%',
                    aspectRatio: '1.91/1',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#f2f2f2',
                    color: '#667781',
                  }}
                >
                  <i className="fas fa-video" style={{ fontSize: '24px', marginBottom: '8px' }}></i>
                  <span style={{ fontSize: '14px' }}>Video</span>
                </div>
              )}
            </div>
          );
        case 'DOCUMENT':
          return (
            <div className="header-document">
              <i className="fas fa-file-alt"></i>
              <span>{header}</span>
            </div>
          );
        default:
          return (
            header && (
              <h5 className="header" style={{ lineHeight: '15px' }}>
                {header}
              </h5>
            )
          );
      }
    };

    const renderButton = (button, index) => {
      const buttonIcon = {
        QUICK_REPLY: 'fa-reply',
        URL: 'fa-external-link-alt',
        PHONE_NUMBER: 'fa-phone',
      }[button.type];

      return (
        <button
          key={index}
          className="template-button"
          title={
            button.type === 'URL'
              ? button.url
              : button.type === 'PHONE_NUMBER'
              ? button.phoneNumber
              : ''
          }
        >
          {buttonIcon && <i className={`fas ${buttonIcon}`} style={{ marginRight: '8px' }}></i>}
          {button.text}
        </button>
      );
    };

    const replaceVariables = (text) => {
      if (!text || variableEntries.length === 0) return text;

      let result = text;
      for (let i = 0; i < variableEntries.length; i++) {
        if (variableEntries[i]) {
          const variablePattern = new RegExp(`\\{\\{${i + 1}\\}\\}`, 'g');
          result = result.replace(variablePattern, variableEntries[i]);
        }
      }
      return result;
    };

    const getAuthenticationBodyText = () => {
      const code = variableEntries[0] || '123456';
      let text = `*${code}* is your verification code.`;
      if (securityRecommendation) {
        text += ' For your security, do not share this code.';
      }
      return text;
    };

    const getAuthenticationFooterText = () => {
      if (showExpiryTime) {
        return `This code expires in ${codeExp || 10} minutes.`;
      }
      return '';
    };

    const parseWhatsAppFormat = (text) => {
      if (!text) return text;

      const textWithVariablesReplaced = replaceVariables(text);

      const segments = [];
      let currentIndex = 0;

      const formatRegex = /(\*[^*]+\*)|(_[^_]+_)|(~[^~]+~)|(```[^`]+```)/g;
      let match;

      while ((match = formatRegex.exec(textWithVariablesReplaced)) !== null) {
        if (match.index > currentIndex) {
          segments.push({
            text: textWithVariablesReplaced.slice(currentIndex, match.index),
            format: 'none',
          });
        }

        const [fullMatch] = match;
        let format;
        let text;

        if (fullMatch.startsWith('```')) {
          format = 'monospace';
          text = fullMatch.slice(3, -3);
        } else {
          format = fullMatch.startsWith('*')
            ? 'bold'
            : fullMatch.startsWith('_')
            ? 'italic'
            : 'strikethrough';
          text = fullMatch.slice(1, -1);
        }

        segments.push({ text, format });

        currentIndex = match.index + fullMatch.length;
      }

      if (currentIndex < textWithVariablesReplaced.length) {
        segments.push({
          text: textWithVariablesReplaced.slice(currentIndex),
          format: 'none',
        });
      }

      return segments.map((segment, index) => {
        switch (segment.format) {
          case 'bold':
            return <strong key={index}>{segment.text}</strong>;
          case 'italic':
            return <i key={index}>{segment.text}</i>;
          case 'strikethrough':
            return <s key={index}>{segment.text}</s>;
          case 'monospace':
            return (
              <code
                key={index}
                style={{
                  backgroundColor: '#f0f0f0',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  fontFamily: 'monospace',
                  color: 'black',
                }}
              >
                {segment.text}
              </code>
            );
          default:
            return <span key={index}>{segment.text}</span>;
        }
      });
    };

    return (
      <div className="wa-template-preview">
        <div className="phone-frame">
          <img src={iPhoneFrame} alt="iPhone frame" className="frame-image" />
          <div className="phone-content">
            <div className="user-profile">
              <div className="avatar">
                <img src={avatarSvg} alt="User avatar" />
              </div>
            </div>

            <div className="message-container">
              <div
                className="message-bubble"
                style={{
                  padding:
                    headerType === 'IMAGE' || headerType === 'VIDEO' ? '0 0 12px 0' : undefined,
                }}
              >
                {renderHeader()}

                <div
                  className="message-content"
                  style={{
                    padding:
                      headerType === 'IMAGE' || headerType === 'VIDEO' ? '0 12px' : undefined,
                  }}
                >
                  {isAuthentication ? (
                    <p className="body-text" style={{ lineHeight: '15px' }}>
                      {parseWhatsAppFormat(getAuthenticationBodyText())}
                    </p>
                  ) : body ? (
                    body.split('\n').map((text, index) => (
                      <p key={index} className="body-text" style={{ lineHeight: '15px' }}>
                        {parseWhatsAppFormat(text)}
                      </p>
                    ))
                  ) : (
                    <p className="body-text empty-body" style={{ lineHeight: '15px' }}>
                      No message content
                    </p>
                  )}

                  {isAuthentication && showExpiryTime ? (
                    <p className="footer" style={{ lineHeight: '15px', marginBottom: '10px' }}>
                      {getAuthenticationFooterText()}
                    </p>
                  ) : footer ? (
                    <p className="footer" style={{ lineHeight: '15px', marginBottom: '10px' }}>
                      {footer}
                    </p>
                  ) : null}

                  <div className="timestamp" style={{ lineHeight: '5px' }}>
                    {new Date().toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </div>
                </div>

                {buttons?.length > 0 && (
                  <div className="wa-template-button-container">
                    {buttons.map((button, index) => renderButton(button, index))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

WhatsAppTemplatePreview.propTypes = {
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  headerType: PropTypes.oneOf(['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT']),
  headerMediaUrl: PropTypes.string,
  body: PropTypes.string,
  footer: PropTypes.string,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['QUICK_REPLY', 'URL', 'PHONE_NUMBER']).isRequired,
      text: PropTypes.string.isRequired,
      url: PropTypes.string,
      phoneNumber: PropTypes.string,
    })
  ),
  variableEntries: PropTypes.arrayOf(PropTypes.string),
  isAuthentication: PropTypes.bool,
  securityRecommendation: PropTypes.bool,
  codeExp: PropTypes.number,
  showExpiryTime: PropTypes.bool,
};

WhatsAppTemplatePreview.displayName = 'WhatsAppTemplatePreview';

export default WhatsAppTemplatePreview;
